import { useState, useEffect, ReactNode } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  InputAdornment,
  Tabs,
  Tab,
  Autocomplete,
  createFilterOptions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// Default departments and designations
const DEFAULT_DEPARTMENTS = [
  'Engineering',
  'Finance',
  'Human Resources',
  'Marketing',
  'Operations',
  'Sales',
  'Trading',
  'Compliance',
  'IT',
  'Admin',
];

const DEFAULT_DESIGNATIONS = [
  'Software Engineer',
  'Senior Software Engineer',
  'Team Lead',
  'Manager',
  'Senior Manager',
  'Director',
  'Vice President',
  'Analyst',
  'Senior Analyst',
  'Associate',
  'Intern',
  'Accountant',
  'HR Executive',
  'Trading Analyst',
  'Compliance Officer',
];

// Default bank names (common Indian banks)
const DEFAULT_BANKS = [
  'State Bank of India (SBI)',
  'HDFC Bank',
  'ICICI Bank',
  'Axis Bank',
  'Kotak Mahindra Bank',
  'IndusInd Bank',
  'Yes Bank',
  'IDFC FIRST Bank',
  'Federal Bank',
  'RBL Bank',
  'Punjab National Bank (PNB)',
  'Bank of Baroda',
  'Canara Bank',
  'Union Bank of India',
  'Bank of India',
  'Indian Bank',
  'Central Bank of India',
  'Indian Overseas Bank',
  'UCO Bank',
  'Bank of Maharashtra',
];

// Filter for autocomplete with "Add new" option
const filter = createFilterOptions<string>();

// Local storage keys
const DEPARTMENTS_KEY = 'percent_investments_departments';
const DESIGNATIONS_KEY = 'percent_investments_designations';
const BANKS_KEY = 'percent_investments_banks';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Employee, EmployeeCreateInput, UserRole } from '../types';
import { salaryService } from '../services/salary.service';
import { formatCurrency, getTodayDate, toTitleCase } from '../utils/formatters';
import { MetricCard } from '../components/common/MetricCard';

// Role options shown to HR/Admin when setting an employee's system access.
// Super Admin is intentionally omitted — it's hardcoded by email.
const ROLE_OPTIONS = [
  { value: UserRole.EMPLOYEE, label: 'Employee', description: 'View own payslips only' },
  { value: UserRole.DEALER,   label: 'Dealer',   description: 'Trading desk — Dashboard, Trades, Market, Risk, Clients' },
  { value: UserRole.HR,       label: 'HR',       description: 'Employees, Attendance, Salary Slips' },
  { value: UserRole.ADMIN,    label: 'Admin',    description: 'Full access (below Super Admin)' },
];

// Allowed email exceptions (non-company domain)
// Validation schema — ONLY Name + Email are required. Everything else
// (designation, joining date, salary, bank, etc.) is optional and can be
// edited or cleared later. Format hints (phone digits, IFSC pattern, PAN
// pattern) are still validated when a value IS supplied — those prevent
// invalid junk going into payroll downstream.
const employeeSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address').optional().or(z.literal('')),
  phone: z.string().regex(/^[0-9]{10}$/, 'Phone must be 10 digits').optional().or(z.literal('')),
  designation: z.string().optional().or(z.literal('')),
  department: z.string().optional(),
  joiningDate: z.string().optional().or(z.literal('')),
  bankName: z.string().optional(),
  bankAccountNo: z.string().optional(),
  ifscCode: z.string().optional(),
  panNumber: z.string().optional(),
  pfNumber: z.string().optional(),
  // Kotak-style payslip fields (all optional, added in HR revamp)
  employeeCode: z.string().optional(),
  grade: z.string().optional(),
  uanNumber: z.string().optional(),
  esicNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  baseSalary: z.number().min(0).optional().or(z.literal(0)),
  systemRole: z.nativeEnum(UserRole).optional(),
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export function EmployeeManagement() {
  const [_loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Department, Designation, and Bank options
  const [departments, setDepartments] = useState<string[]>(() => {
    const stored = localStorage.getItem(DEPARTMENTS_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_DEPARTMENTS;
  });
  const [designations, setDesignations] = useState<string[]>(() => {
    const stored = localStorage.getItem(DESIGNATIONS_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_DESIGNATIONS;
  });
  const [banks, setBanks] = useState<string[]>(() => {
    const stored = localStorage.getItem(BANKS_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_BANKS;
  });

  // Save departments/designations/banks to localStorage when they change
  useEffect(() => {
    localStorage.setItem(DEPARTMENTS_KEY, JSON.stringify(departments));
  }, [departments]);

  useEffect(() => {
    localStorage.setItem(DESIGNATIONS_KEY, JSON.stringify(designations));
  }, [designations]);

  useEffect(() => {
    localStorage.setItem(BANKS_KEY, JSON.stringify(banks));
  }, [banks]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    mode: 'onChange', // Validate on change for better UX
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      designation: '',
      department: '',
      joiningDate: getTodayDate(),
      bankName: '',
      bankAccountNo: '',
      ifscCode: '',
      panNumber: '',
      pfNumber: '',
      employeeCode: '',
      grade: '',
      uanNumber: '',
      esicNumber: '',
      dateOfBirth: '',
      baseSalary: 0,
      systemRole: UserRole.EMPLOYEE,
    },
  });

  // Helper to determine which tab has errors
  const getTabWithErrors = () => {
    const errorKeys = Object.keys(errors);

    // Tab 0: Basic Info
    const tab0Fields = ['name', 'email', 'phone', 'designation', 'department', 'joiningDate', 'systemRole'];
    if (errorKeys.some(key => tab0Fields.includes(key))) return 0;

    // Tab 1: Bank Details + Identity (Kotak fields live here too)
    const tab1Fields = ['bankName', 'bankAccountNo', 'ifscCode', 'panNumber', 'pfNumber',
                        'employeeCode', 'grade', 'uanNumber', 'esicNumber', 'dateOfBirth'];
    if (errorKeys.some(key => tab1Fields.includes(key))) return 1;

    // Tab 2: Salary
    const tab2Fields = ['baseSalary'];
    if (errorKeys.some(key => tab2Fields.includes(key))) return 2;

    return null;
  };

  // Check if a tab has errors
  const tabHasErrors = (tabIndex: number) => {
    const errorKeys = Object.keys(errors);

    if (tabIndex === 0) {
      const tab0Fields = ['name', 'email', 'phone', 'designation', 'department', 'joiningDate', 'systemRole'];
      return errorKeys.some(key => tab0Fields.includes(key));
    }
    if (tabIndex === 1) {
      const tab1Fields = ['bankName', 'bankAccountNo', 'ifscCode', 'panNumber', 'pfNumber',
                          'employeeCode', 'grade', 'uanNumber', 'esicNumber', 'dateOfBirth'];
      return errorKeys.some(key => tab1Fields.includes(key));
    }
    if (tabIndex === 2) {
      const tab2Fields = ['baseSalary'];
      return errorKeys.some(key => tab2Fields.includes(key));
    }
    return false;
  };

  const watchedDesignation = watch('designation');
  const watchedDepartment = watch('department');
  const watchedBankName = watch('bankName');

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { employees } = await salaryService.getEmployees();
        setEmployees(employees);
      } catch (err: any) {
        setError(err.message || 'Failed to load employees');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenDialog = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      reset({
        name: employee.name,
        email: employee.email,
        phone: employee.phone || '',
        designation: employee.designation,
        department: employee.department || '',
        joiningDate: employee.joiningDate.split('T')[0],
        bankName: employee.bankName || '',
        bankAccountNo: employee.bankAccountNo || '',
        ifscCode: employee.ifscCode || '',
        panNumber: employee.panNumber || '',
        pfNumber: employee.pfNumber || '',
        employeeCode: employee.employeeCode || '',
        grade: employee.grade || '',
        uanNumber: employee.uanNumber || '',
        esicNumber: employee.esicNumber || '',
        dateOfBirth: employee.dateOfBirth ? employee.dateOfBirth.split('T')[0] : '',
        baseSalary: employee.baseSalary,
        systemRole: employee.systemRole || UserRole.EMPLOYEE,
      });
    } else {
      setEditingEmployee(null);
      reset({
        name: '',
        email: '',
        phone: '',
        designation: '',
        department: '',
        joiningDate: getTodayDate(),
        bankName: '',
        bankAccountNo: '',
        ifscCode: '',
        panNumber: '',
        pfNumber: '',
        employeeCode: '',
        grade: '',
        uanNumber: '',
        esicNumber: '',
        dateOfBirth: '',
        baseSalary: 0,
        systemRole: UserRole.EMPLOYEE,
      });
    }
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
    reset();
  };

  // Helper to format validation errors from backend
  const formatValidationError = (err: any): string => {
    console.log('[EmployeeManagement] Error received:', err);

    // Check if error response has nested error object
    const errorData = err.response?.data?.error || err.response?.data || err;

    // Check if it's a validation error with field details (nested under details.fields)
    const fields = errorData.details?.fields || errorData.fields;
    if (fields && typeof fields === 'object') {
      const fieldErrors = Object.entries(fields)
        .map(([field, message]) => {
          // Extract user-friendly message from Joi error format
          const msg = typeof message === 'string' ? message : String(message);

          // Provide user-friendly error messages based on field
          if (field === 'ifscCode' && msg.includes('pattern')) {
            return '• IFSC Code: Must be 11 characters (e.g., HDFC0001234)';
          }
          if (field === 'panNumber' && msg.includes('pattern')) {
            return '• PAN Number: Must be 10 characters (e.g., ABCDE1234F)';
          }
          if (field === 'phone' && msg.includes('10 digits')) {
            return '• Phone: Must be exactly 10 digits (e.g., 9876543210)';
          }
          if (field === 'email' && msg.includes('domain')) {
            return '• Email: Must be @percentinvestments.com or authorized exception';
          }

          // Remove quotes and "with value" part for other fields
          const cleanMsg = msg
            .replace(/^"[^"]*"\s+/, '') // Remove field name in quotes
            .replace(/\s+with value\s+"[^"]*"\s+/, ' ') // Remove "with value" part
            .replace(/"/g, '') // Remove remaining quotes
            .replace(/\s+fails to match.*$/, 'Invalid format'); // Simplify pattern errors

          const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');
          return `• ${fieldName}: ${cleanMsg}`;
        })
        .join('\n');
      return `Please fix these errors:\n\n${fieldErrors}`;
    }

    // Check for error message with details
    if (errorData.message) {
      // If message contains validation details, return it
      if (errorData.message.includes('validation') || errorData.message.includes('fails to match')) {
        return errorData.message.replace(/"/g, '');
      }
      return errorData.message;
    }

    // Check direct message property
    if (err.message) {
      return err.message;
    }

    return 'Failed to save employee. Please check all fields and try again.';
  };

  const onSubmit = async (data: EmployeeFormData) => {
    // If there are validation errors, navigate to the first tab with errors
    const errorTab = getTabWithErrors();
    if (errorTab !== null) {
      setTabValue(errorTab);
      setError('Please fix all validation errors before submitting');
      return;
    }

    setSaving(true);
    try {
      // Add new department/designation/bank if not in list
      if (data.department && !departments.includes(data.department)) {
        setDepartments((prev) => [...prev, data.department!].sort());
      }
      if (data.designation && !designations.includes(data.designation)) {
        setDesignations((prev) => [...prev, data.designation!].sort());
      }
      if (data.bankName && !banks.includes(data.bankName)) {
        setBanks((prev) => [...prev, data.bankName!].sort());
      }

      // Prepare the payload with normalized email and uppercase IFSC/PAN.
      // For UPDATE, omit fields the user left blank so the deployed backend's
      // strict Joi rules (designation: min 2 chars, baseSalary: positive())
      // don't reject the save. Empty fields keep their existing DB value.
      const blank = (v: unknown) => v === undefined || v === null || v === '' || (typeof v === 'string' && v.trim() === '');
      const newBase = !data.baseSalary || data.baseSalary <= 0 ? undefined : data.baseSalary;

      // CRITICAL: when baseSalary is set/changed, the component breakdown must
      // sum to the new base — payroll multiplies the COMPONENTS, not baseSalary,
      // so a stale split keeps paying the old amount (the Anand ₹1,00,000-vs-
      // ₹15,000 bug). Re-derive the Indian-standard split (40 / 16 / 37.20%)
      // whenever the existing components don't already sum to the new base
      // (covers both the "all zero" case and a leftover split for a different
      // salary). A deliberate custom split that already sums to base is kept.
      const sc = editingEmployee?.salaryComponents;
      const existingBaseSum = sc
        ? (sc.basic || 0) + (sc.hra || 0) + (sc.specialAllowance || 0) + (sc.otherAllowances || 0)
        : 0;
      let derivedComponents: any = undefined;
      if (newBase && newBase > 0 && (!editingEmployee || existingBaseSum !== newBase)) {
        const basic = Math.round(newBase * 0.40);
        const hra = Math.round(newBase * 0.16);
        const specialAllowance = Math.round(newBase * 0.3720);
        const otherAllowances = Math.max(0, newBase - basic - hra - specialAllowance);
        derivedComponents = { basic, hra, da: 0, specialAllowance, bonus: 0, otherAllowances };
      }

      const baseFields = {
        ...data,
        name: toTitleCase(data.name),
        email: blank(data.email) ? undefined : (data.email || '').toLowerCase(),
        designation: blank(data.designation) ? undefined : data.designation,
        baseSalary: newBase,
        salaryComponents: derivedComponents,
        ifscCode: blank(data.ifscCode) ? undefined : data.ifscCode!.toUpperCase(),
        panNumber: blank(data.panNumber) ? undefined : data.panNumber!.toUpperCase(),
        joiningDate: blank(data.joiningDate) ? undefined : data.joiningDate,
      };
      // Strip undefined keys so they don't hit the backend at all
      const payload = Object.fromEntries(
        Object.entries(baseFields).filter(([, v]) => v !== undefined)
      ) as typeof baseFields;

      if (editingEmployee) {
        const { employee } = await salaryService.updateEmployee(editingEmployee.employeeId, payload);
        setEmployees((prev) =>
          prev.map((e) => (e.employeeId === editingEmployee.employeeId ? employee : e))
        );
        setSuccess('Employee updated successfully');
      } else {
        const { employee } = await salaryService.createEmployee(payload as EmployeeCreateInput);
        setEmployees((prev) => [employee, ...prev]);
        setSuccess('Employee created successfully');
      }
      handleCloseDialog();
    } catch (err: any) {
      setError(formatValidationError(err));
    } finally {
      setSaving(false);
    }
  };

  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (permanent = false) => {
    if (!employeeToDelete) return;
    setDeleting(true);
    try {
      await salaryService.deleteEmployee(employeeToDelete.employeeId, permanent);
      setEmployees((prev) => prev.filter((e) => e.employeeId !== employeeToDelete.employeeId));
      setSuccess(permanent ? 'Employee permanently deleted' : 'Employee deactivated successfully');
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete employee');
    } finally {
      setDeleting(false);
    }
  };

  // Helper to get role display info
  const getRoleDisplay = (role?: UserRole) => {
    // Neutral palette — outlined chips, no purple/pink.
    // ALL roles must be enumerated explicitly. Missing a case silently falls
    // back to 'Employee' which is the bug we hit with HR/Dealer.
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return { label: 'Super Admin', color: '#991B1B', bg: '#FEF2F2' };
      case UserRole.ADMIN:
        return { label: 'Admin', color: '#0A0A0A', bg: '#F4F4F5' };
      case UserRole.HR:
        return { label: 'HR', color: '#0A0A0A', bg: '#F4F4F5' };
      case UserRole.DEALER:
        return { label: 'Dealer', color: '#0A0A0A', bg: '#F4F4F5' };
      case UserRole.AGENT:
        return { label: 'Agent', color: '#0A0A0A', bg: '#F4F4F5' };
      case UserRole.CLIENT:
        return { label: 'Client', color: '#737373', bg: '#FAFAFA' };
      case UserRole.EMPLOYEE:
      default:
        return { label: 'Employee', color: '#737373', bg: '#FAFAFA' };
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: 'Email',
      field: 'email',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Designation',
      field: 'designation',
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: 'Department',
      field: 'department',
      sortable: true,
      filter: true,
      width: 130,
    },
    {
      headerName: 'System Role',
      field: 'systemRole',
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: (params: ICellRendererParams) => {
        const display = getRoleDisplay(params.value);
        return (
          <Chip
            label={display.label}
            size="small"
            sx={{
              fontWeight: 600,
              bgcolor: display.bg,
              color: display.color,
            }}
          />
        );
      },
    },
    {
      headerName: 'Base Salary',
      field: 'baseSalary',
      sortable: true,
      width: 130,
      cellRenderer: (params: ICellRendererParams) => formatCurrency(params.value),
    },
    {
      headerName: 'Status',
      field: 'isActive',
      width: 100,
      cellRenderer: (params: ICellRendererParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          size="small"
          color={params.value ? 'success' : 'default'}
          sx={{ fontWeight: 500 }}
        />
      ),
    },
    {
      headerName: 'Actions',
      field: 'actions',
      width: 120,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.data)}
            sx={{ color: '#737373', '&:hover': { color: '#0A0A0A' } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setEmployeeToDelete(params.data);
              setDeleteDialogOpen(true);
            }}
            sx={{ color: '#737373', '&:hover': { color: '#DC2626' } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const activeEmployees = employees.filter((e) => e.isActive);
  const totalSalary = activeEmployees.reduce((sum, e) => sum + e.baseSalary, 0);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} color="#0A0A0A" sx={{ letterSpacing: -0.4 }}>
            Employees
          </Typography>
          <Typography variant="body2" color="#737373">
            Manage employee information and salary details
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#0A0A0A',
            '&:hover': { bgcolor: '#262626' },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Add Employee
        </Button>
      </Box>

      {/* Metrics — restyled to neutral palette */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Employees"
            value={employees.length}
            icon={<PersonIcon />}
            color="#0A0A0A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Employees"
            value={activeEmployees.length}
            icon={<PersonIcon />}
            color="#0A0A0A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Monthly Payroll"
            value={formatCurrency(totalSalary)}
            icon={<AccountBalanceIcon />}
            color="#0A0A0A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg. Salary"
            value={formatCurrency(activeEmployees.length > 0 ? totalSalary / activeEmployees.length : 0)}
            icon={<AccountBalanceIcon />}
            color="#0A0A0A"
          />
        </Grid>
      </Grid>

      {/* Alerts */}
      <Snackbar
        open={!!error}
        autoHideDuration={10000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            whiteSpace: 'pre-line',
            maxWidth: 500,
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
            {error}
          </Typography>
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Data Grid */}
      <Card sx={{ borderRadius: '16px', border: '1px solid #E5E7EB' }}>
        <CardContent>
          <Box
            className="ag-theme-alpine"
            sx={{
              height: 500,
              '& .ag-header-cell-label': { fontWeight: 600 },
              '& .ag-root-wrapper': { border: 'none', borderRadius: '8px' },
            }}
          >
            <AgGridReact
              rowData={employees}
              columnDefs={columnDefs}
              defaultColDef={{
                resizable: true,
              }}
              animateRows
              pagination
              paginationPageSize={20}
              domLayout="normal"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>
          {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ pt: 3 }}>
            {/* Validation Error Summary */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Please fix the following errors:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>
                      <Typography variant="body2">
                        <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong> {error.message}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Basic Info
                    {tabHasErrors(0) && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#EF4444',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Bank Details
                    {tabHasErrors(1) && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#EF4444',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Salary
                    {tabHasErrors(2) && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#EF4444',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message || 'Any valid email address (internal or external)'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ style: { textTransform: 'lowercase' } }}
                    placeholder="e.g., john@example.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    {...register('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={designations}
                    value={watchedDesignation || ''}
                    onChange={(_, newValue) => {
                      setValue('designation', newValue || '');
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      // Suggest adding new option
                      const isExisting = options.some((option) => inputValue === option);
                      if (inputValue !== '' && !isExisting) {
                        filtered.push(`Add "${inputValue}"`);
                      }
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation"
                        error={!!errors.designation}
                        helperText={errors.designation?.message || 'Select or type to add new'}
                      />
                    )}
                    onInputChange={(_, value) => {
                      // Handle "Add new" option
                      if (value.startsWith('Add "') && value.endsWith('"')) {
                        const newValue = value.slice(5, -1);
                        setValue('designation', newValue);
                        if (!designations.includes(newValue)) {
                          setDesignations((prev) => [...prev, newValue].sort());
                        }
                      } else {
                        setValue('designation', value);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={departments}
                    value={watchedDepartment || ''}
                    onChange={(_, newValue) => {
                      setValue('department', newValue || '');
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      // Suggest adding new option
                      const isExisting = options.some((option) => inputValue === option);
                      if (inputValue !== '' && !isExisting) {
                        filtered.push(`Add "${inputValue}"`);
                      }
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Department"
                        helperText="Select or type to add new"
                      />
                    )}
                    onInputChange={(_, value) => {
                      // Handle "Add new" option
                      if (value.startsWith('Add "') && value.endsWith('"')) {
                        const newValue = value.slice(5, -1);
                        setValue('department', newValue);
                        if (!departments.includes(newValue)) {
                          setDepartments((prev) => [...prev, newValue].sort());
                        }
                      } else {
                        setValue('department', value);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Joining Date"
                    type="date"
                    {...register('joiningDate')}
                    error={!!errors.joiningDate}
                    helperText={errors.joiningDate?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="systemRole"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>System Access Role</InputLabel>
                        <Select
                          {...field}
                          label="System Access Role"
                          startAdornment={
                            <InputAdornment position="start">
                              <AdminPanelSettingsIcon sx={{ color: '#9CA3AF' }} />
                            </InputAdornment>
                          }
                        >
                          {ROLE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box>
                                <Typography fontWeight={600}>{option.label}</Typography>
                                <Typography variant="caption" color="#6B7280">
                                  {option.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Typography variant="caption" color="#6B7280" sx={{ mt: 0.5, display: 'block' }}>
                    This role determines what system access the employee will have when they sign up
                  </Typography>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={banks}
                    value={watchedBankName || ''}
                    onChange={(_, newValue) => {
                      setValue('bankName', newValue || '');
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      // Suggest adding new option
                      const isExisting = options.some((option) => inputValue === option);
                      if (inputValue !== '' && !isExisting) {
                        filtered.push(`Add "${inputValue}"`);
                      }
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bank Name"
                        placeholder="e.g., HDFC Bank, ICICI Bank, SBI"
                        helperText="Select or type to add new bank"
                      />
                    )}
                    onInputChange={(_, value) => {
                      // Handle "Add new" option
                      if (value.startsWith('Add "') && value.endsWith('"')) {
                        const newValue = value.slice(5, -1);
                        setValue('bankName', newValue);
                        if (!banks.includes(newValue)) {
                          setBanks((prev) => [...prev, newValue].sort());
                        }
                      } else {
                        setValue('bankName', value);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    {...register('bankAccountNo')}
                    placeholder="e.g., 1234567890123456"
                    helperText="Bank account number (10-18 digits)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="IFSC Code"
                    {...register('ifscCode')}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    placeholder="e.g., HDFC0001234"
                    helperText="11 characters: 4 letters + 0 + 6 alphanumeric"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PAN Number"
                    {...register('panNumber')}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    placeholder="e.g., ABCDE1234F"
                    helperText="10 characters: 5 letters + 4 digits + 1 letter"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PF Number"
                    {...register('pfNumber')}
                    placeholder="e.g., BGBNG12345670000123"
                    helperText="Provident Fund account number (optional)"
                  />
                </Grid>

                {/* Kotak-style payslip identity fields */}
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#737373', fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                    Payslip identifiers (optional)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Employee Code"
                    {...register('employeeCode')}
                    placeholder="e.g., PI001"
                    helperText="Human-readable code shown on the payslip"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Grade"
                    {...register('grade')}
                    placeholder="e.g., M3"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="UAN Number"
                    {...register('uanNumber')}
                    placeholder="12-digit UAN"
                    helperText="Universal Account Number for PF portability"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ESIC Account Number"
                    {...register('esicNumber')}
                    placeholder="ESIC account"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Birth"
                    {...register('dateOfBirth')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="baseSalary"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        label="Monthly Base Salary (CTC)"
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        error={!!errors.baseSalary}
                        helperText={errors.baseSalary?.message || 'Basic, HRA, DA will be calculated automatically'}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: '8px' }}>
                    <Typography variant="body2">
                      <strong>Salary Breakdown (Auto-calculated):</strong>
                      <br />
                      Basic: 40% of CTC · HRA: 16% of CTC · Special Allowance: 37.20% of CTC · Bonus: 0
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </TabPanel>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E5E5' }}>
            <Button onClick={handleCloseDialog} disabled={saving} sx={{ color: '#737373', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : undefined}
              sx={{
                bgcolor: '#0A0A0A',
                '&:hover': { bgcolor: '#262626' },
                borderRadius: '8px',
                textTransform: 'none',
              }}
            >
              {saving ? 'Saving…' : (editingEmployee ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 450 } }}
      >
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            What would you like to do with <strong>{employeeToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
            <strong>Deactivate:</strong> Marks the employee as inactive. They can be reactivated later.
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            <strong>Permanently Delete:</strong> Removes the employee record completely. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ color: '#737373', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(false)}
              variant="outlined"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={14} sx={{ color: '#0A0A0A' }} /> : undefined}
              sx={{ borderColor: '#E5E5E5', color: '#0A0A0A', textTransform: 'none', '&:hover': { borderColor: '#0A0A0A', bgcolor: 'transparent' } }}
            >
              {deleting ? 'Working…' : 'Deactivate'}
            </Button>
            <Button
              onClick={() => handleDelete(true)}
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : undefined}
              sx={{ bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' }, textTransform: 'none' }}
            >
              {deleting ? 'Deleting…' : 'Permanently Delete'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeeManagement;
